'use client'

import React from 'react'
import { AppProvider, Page, Card, Layout, BlockStack, Text, Button, InlineStack } from '@shopify/polaris'
import '@shopify/polaris/build/esm/styles.css'
import enTranslations from '@shopify/polaris/locales/en.json'
import { PolarisLink } from '@/components/PolarisLink'
import { redirectToUrl } from '@/app/(app)/hooks/useAppBridgeRedirect'

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.sigmasense.pk'

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('[AppErrorBoundary]', error, errorInfo)
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  handleReconnect = () => {
    if (typeof window === 'undefined') return
    try {
      const params = new URLSearchParams(window.location.search)
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const shop = params.get('shop') || hashParams.get('shop') || window.localStorage.getItem('shopify-shop')
      const host = params.get('host') || hashParams.get('host') || window.localStorage.getItem('shopify-host')
      if (shop && host) {
        const url = `${APP_URL}/api/auth?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}`
        redirectToUrl(url, { host, shop })
      } else if (shop) {
        window.location.href = `${APP_URL.replace(/\/$/, '')}/signin`
      } else {
        window.location.reload()
      }
    } catch {
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <AppProvider i18n={enTranslations} linkComponent={PolarisLink as any}>
          <Page title="Something went wrong">
            <Layout>
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <Text as="p" variant="bodyMd" tone="subdued">
                      An unexpected error occurred. Try reloading the page or reconnecting to the app.
                    </Text>
                    <InlineStack gap="300">
                      <Button onClick={this.handleReload}>Reload</Button>
                      <Button variant="primary" onClick={this.handleReconnect}>
                        Reconnect
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        </AppProvider>
      )
    }
    return this.props.children
  }
}
